/**
 * unbilled_work — every vertical. GET /dashboard/unbilled-work (api#1125):
 * work owed an invoice right now, with no window.
 *
 * The headline is the API's `total`, per currency, never summed across them.
 * Each vertical is a line in its OWN unit — casing and downhole and pressure
 * testing count tickets, trucking counts BOLs, rentals count orders — so the
 * counts are never added into one number: "13 tickets and 7 orders" is not 20
 * of anything.
 *
 * UNPRICED IS SAID AS UNPRICED. Rows with no figure are in a line's count and
 * read "+N unpriced" beside its money; they are in no amount and never $0. A
 * vertical that is not `ok` is named under the lines (MoneyVerticalNotes) and
 * draws no line of its own, because its figure is not one.
 */
import { fmtCurrency } from '../../../lib/display-labels';
import { fmtAmounts } from '../../../lib/currency-amounts';
import { queryFailureError, queryLoadFailure } from '../../../lib/load-failure';
import type { MoneyVertical, UnbilledWork, VerticalUnbilled } from '../../../types/overview-money';
import { LoadFailureNotice } from '../../ui/LoadFailureNotice';
import { MoneyVerticalNotes, moneyVerticalLabel } from '../MoneyVerticalNotes';
import { WidgetPanel, WidgetPending } from '../WidgetPanel';
import { useUnbilledWork } from '../overviewQueries';

/** Exhaustive over the route's verticals: what one owed row is, in each. */
const UNIT: Record<MoneyVertical, [string, string]> = {
  casing_running: ['ticket', 'tickets'],
  trucking: ['BOL', 'BOLs'],
  surface_rentals: ['order', 'orders'],
  downhole_tools: ['ticket', 'tickets'],
  pressure_testing: ['ticket', 'tickets'],
  ndt_inspection: ['ticket', 'tickets'],
  pipeline_facility: ['ticket', 'tickets'],
  well_testing: ['ticket', 'tickets'],
  well_servicing: ['ticket', 'tickets'],
};
const unitOf = (v: string, n: number) => {
  const u = UNIT[v as MoneyVertical] ?? ['item', 'items'];
  return `${n} ${n === 1 ? u[0] : u[1]}`;
};

/** A line's money: a real $0.00 only when nothing is owed; money missing from owed rows is —. */
function lineMoney(v: VerticalUnbilled): string {
  const priced = v.count - v.unpriced_count;
  return fmtAmounts(v.amounts, priced > 0 ? '—' : v.count === 0 ? fmtCurrency(0) : '');
}

export function UnbilledLines({ read }: { read: UnbilledWork }) {
  const lines = read.verticals.filter((v) => v.status === 'ok' || v.status === 'partial');
  return (
    <>
      <p data-testid="unbilled-total" className="text-2xl font-extrabold tabular-nums text-fieldiq-text">
        {fmtAmounts(read.total, read.complete ? fmtCurrency(0) : '—')}
      </p>
      <p className="text-xs text-fieldiq-muted">owed an invoice now</p>
      {lines.length > 0 && (
        <ul data-testid="unbilled-lines" className="mt-3 space-y-1.5 text-xs">
          {lines.map((v) => {
            const money = lineMoney(v);
            return (
              <li key={v.vertical} className="flex items-baseline justify-between gap-3">
                <span className="text-fieldiq-muted">
                  {moneyVerticalLabel(v.vertical)} · {unitOf(v.vertical, v.count)}
                </span>
                <span className="text-right font-bold tabular-nums text-fieldiq-text">
                  {money}
                  {v.unpriced_count > 0 && (
                    <span className="font-semibold text-fieldiq-muted">
                      {money ? ' ' : ''}+{v.unpriced_count} unpriced
                    </span>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      <MoneyVerticalNotes verticals={read.verticals} testId="unbilled-vertical-notes" />
    </>
  );
}

export default function UnbilledWorkWidget() {
  const q = useUnbilledWork();
  const failure = q.data === undefined ? queryLoadFailure(q) : null;

  return (
    <WidgetPanel
      widgetKey="unbilled_work"
      eyebrow="Ready to bill"
      title="Unbilled Work"
      link={{ label: 'Invoices', to: '/finance/invoices' }}
    >
      <div className="p-4">
        {failure ? (
          <LoadFailureNotice error={queryFailureError(q)} kind={failure} subject="unbilled work" className="py-6" allowed="admins, office staff, supervisors and dispatchers" />
        ) : q.data === undefined ? (
          <WidgetPending rows={2} />
        ) : q.data === null ? (
          // An envelope with no payload is not "nothing owed".
          <LoadFailureNotice error={new Error('No unbilled-work payload')} kind="unreachable" subject="unbilled work" className="py-6" />
        ) : (
          <UnbilledLines read={q.data} />
        )}
      </div>
    </WidgetPanel>
  );
}
